import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Button, TextField } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";

import { shuffleArray, resolveQuizImageUrl } from "./escapeRoomEditorCanvasInteraction";

function matchTrim(s) {
	return String(s ?? "").trim();
}
function matchHasText(item) {
	return !!matchTrim(item?.text);
}
function matchHasImage(item) {
	return !!(item?.imageUrl && matchTrim(item.imageUrl));
}
function matchUsesRichLayout(leftItems, rightItems) {
	const all = [...(leftItems || []), ...(rightItems || [])];
	return all.some((it) => matchHasImage(it));
}
function splitMatchText(text) {
	const t = matchTrim(text);
	if (!t) return { title: "", subtitle: "" };
	const nl = t.indexOf("\n");
	if (nl === -1) return { title: t, subtitle: "" };
	return { title: t.slice(0, nl).trim(), subtitle: t.slice(nl + 1).trim() };
}
function matchItemFillBg(item) {
	return matchHasText(item) ? "rgba(248, 250, 252, 0.92)" : "transparent";
}
function matchResolvedPortalImg(item) {
	const u = item?.imageUrl && matchTrim(item.imageUrl);
	return u ? resolveQuizImageUrl(u) : null;
}

function matchQuestionStructureKey(firstQ) {
	const L = firstQ?.matchLeft || [];
	const R = firstQ?.matchRight || [];
	const pairs = firstQ?.matchPairs || [];
	return [
		L.map((l) => l.id).join(","),
		R.map((r) => r.id).join(","),
		pairs.map((pr) => `${pr[0]}:${pr[1]}`).join(";"),
	].join("|");
}

function MatchQuizPlay({ firstQ, onConfirm, done, answer, optionTextStyle }) {
	const leftItems = firstQ?.matchLeft || [];
	const rightItems = firstQ?.matchRight || [];
	const correctPairs = new Set((firstQ?.matchPairs || []).map(([l, r]) => `${l}:${r}`));
	const matchStructureKey = matchQuestionStructureKey(firstQ);
	const answerPairsKey = (answer?.pairs || []).map((p) => `${p[0]}:${p[1]}`).join(";");
	const [slotAssignments, setSlotAssignments] = useState({});
	const [pool, setPool] = useState(() => shuffleArray(rightItems.map((r) => r.id)));
	const skipInitialPoolReshuffleRef = useRef(true);
	const hasImages = useMemo(() => matchUsesRichLayout(leftItems, rightItems), [leftItems, rightItems]);
	const useGridLayout = hasImages || leftItems.length >= 2;
	const itemCount = Math.max(leftItems.length, rightItems.length);
	const matchDensity = itemCount >= 4 || (!hasImages && itemCount >= 3) ? "compact" : "normal";
	const densitySizes =
		matchDensity === "compact"
			? {
				slotMinH: hasImages ? 72 : 0,
				gridGap: hasImages ? 6 : 4,
				poolW: hasImages ? 112 : 0,
				poolMinW: hasImages ? 100 : 120,
				poolMaxW: hasImages ? 132 : 168,
				rowGap: 4,
				rowPad: "4px 6px",
				textLineHeight: 1.25,
			}
			: {
				slotMinH: hasImages ? 100 : 0,
				gridGap: hasImages ? 8 : 6,
				poolW: hasImages ? 128 : 0,
				poolMinW: hasImages ? 118 : 128,
				poolMaxW: hasImages ? 148 : 180,
				rowGap: 6,
				rowPad: "6px 8px",
				textLineHeight: 1.3,
			};

	useEffect(() => {
		if (done && answer?.pairs?.length) {
			const obj = {};
			answer.pairs.forEach(([l, r]) => { obj[l] = r; });
			setSlotAssignments(obj);
		}
	}, [done, answerPairsKey]);
	useEffect(() => {
		if (done) return;
		const R = firstQ?.matchRight || [];
		if (skipInitialPoolReshuffleRef.current) {
			skipInitialPoolReshuffleRef.current = false;
			return;
		}
		setPool(shuffleArray(R.map((r) => r.id)));
		setSlotAssignments({});
	}, [matchStructureKey]);
	const prevDoneRef = useRef(done);
	useEffect(() => {
		if (done) {
			prevDoneRef.current = true;
			return;
		}
		if (prevDoneRef.current !== true) return;
		prevDoneRef.current = false;
		const R = firstQ?.matchRight || [];
		setPool(shuffleArray(R.map((r) => r.id)));
		setSlotAssignments({});
	}, [done]);
	const assignedRightIds = Object.values(slotAssignments).filter(Boolean);
	const availablePool = pool.filter((id) => !assignedRightIds.includes(id));
	const textPoolGrid = useGridLayout && !hasImages;
	const availablePoolCount = availablePool.length;
	const getRightById = (id) => rightItems.find((r) => r.id === id);
	const handleDragStart = (e, rightId) => {
		if (done) return;
		e.dataTransfer.setData("application/json", JSON.stringify({ type: "right", id: rightId }));
		e.dataTransfer.effectAllowed = "move";
	};
	const handleSlotDragStart = (e, leftId, rightId) => {
		if (!rightId || done) return;
		e.dataTransfer.setData("application/json", JSON.stringify({ type: "fromSlot", leftId, rightId }));
		e.dataTransfer.effectAllowed = "move";
	};
	const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
	const handleSlotDrop = (e, leftId) => {
		if (done) return;
		e.preventDefault();
		try {
			const d = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
			if (d.type === "right") {
				setSlotAssignments((prev) => ({ ...prev, [leftId]: d.id }));
			} else if (d.type === "fromSlot") {
				setSlotAssignments((prev) => {
					const next = { ...prev };
					delete next[d.leftId];
					next[leftId] = d.rightId;
					return next;
				});
			}
		} catch (_) {}
	};
	const handlePoolDrop = (e) => {
		if (done) return;
		e.preventDefault();
		try {
			const d = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
			if (d.type === "fromSlot") {
				setSlotAssignments((prev) => {
					const next = { ...prev };
					delete next[d.leftId];
					return next;
				});
			}
		} catch (_) {}
	};
	const pairs = leftItems.map((left) => [left.id, slotAssignments[left.id]]).filter(([, r]) => r);
	const canCheck = pairs.length === leftItems.length && leftItems.length > 0;
	const handleCheck = () => {
		if (!canCheck || !onConfirm) return;
		onConfirm({ pairs: pairs.map(([l, r]) => [l, r]) });
	};

	const accentTitle = "#14b8a6";
	const cardBorder = "1px solid rgba(100, 116, 139, 0.4)";
	const imgErr = (e) => {
		e.target.style.display = "none";
	};

	const renderMatchFace = (item, { bracketLabel, imgMaxH = 88, minImgBox = 0 }) => {
		const imgUrl = matchResolvedPortalImg(item);
		const hasImg = !!imgUrl;
		const brTok = bracketLabel ? String(bracketLabel).trim() : "";
		const raw = matchTrim(item?.text);
		let { title, subtitle } = splitMatchText(item?.text);
		if (brTok && matchTrim(title) === brTok) title = "";
		const onlyAutoBracket = !!(brTok && raw === brTok && !hasImg);
		const showTextCard = matchHasText(item) && !onlyAutoBracket && (!!matchTrim(title) || !!matchTrim(subtitle));
		const imgWrapStyle = {
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "2px 0",
			boxSizing: "border-box",
			...(minImgBox > 0 ? { minHeight: minImgBox } : {}),
		};
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					gap: 4,
					width: "100%",
					minWidth: 0,
					boxSizing: "border-box",
				}}
			>
				{hasImg ? (
					<div style={imgWrapStyle}>
						<img
							src={imgUrl}
							alt=""
							draggable={false}
							onError={imgErr}
							style={{
								maxWidth: "100%",
								maxHeight: imgMaxH,
								width: "auto",
								height: "auto",
								objectFit: "contain",
								display: "block",
							}}
						/>
					</div>
				) : null}
				{showTextCard ? (
					<div
						style={{
							width: "100%",
							padding: "6px 8px",
							borderRadius: 8,
							background: "rgba(248, 250, 252, 0.95)",
							border: "1px solid rgba(226, 232, 240, 0.9)",
							boxSizing: "border-box",
						}}
					>
						{matchTrim(subtitle) ? (
							matchTrim(title) ? (
								<>
									<div
										style={{
											fontWeight: 800,
											letterSpacing: "0.06em",
											textTransform: "uppercase",
											fontSize: "0.82em",
											color: accentTitle,
											textAlign: "center",
											lineHeight: 1.25,
										}}
									>
										{title}
									</div>
									<div
										style={{
											marginTop: 6,
											fontSize: "0.88em",
											color: "#475569",
											textAlign: "center",
											lineHeight: 1.35,
											...optionTextStyle,
											fontWeight: optionTextStyle?.fontWeight ?? 400,
										}}
									>
										{subtitle}
									</div>
								</>
							) : (
								<div
									style={{
										fontSize: "0.88em",
										color: "#475569",
										textAlign: "center",
										lineHeight: 1.35,
										...optionTextStyle,
										fontWeight: optionTextStyle?.fontWeight ?? 400,
									}}
								>
									{subtitle}
								</div>
							)
						) : (
							<div
								style={{
									fontWeight: 600,
									textAlign: "center",
									lineHeight: 1.35,
									...optionTextStyle,
								}}
							>
								{title}
							</div>
						)}
					</div>
				) : null}
				{!hasImg && !showTextCard && !bracketLabel ? <span style={{ color: "#94a3b8" }}>—</span> : null}
				{bracketLabel ? (
					<div style={{ fontSize: 12, fontWeight: 700, color: accentTitle, letterSpacing: "0.08em" }}>{bracketLabel}</div>
				) : null}
			</div>
		);
	};

	const slotBg = (right) => {
		if (!right) return "#f1f5f9";
		return matchHasText(right) ? "rgba(255, 255, 255, 0.92)" : "transparent";
	};

	const slotBorder = (right, isCorrect, isWrong) => {
		if (isCorrect) return "#22c55e";
		if (isWrong) return "#ef4444";
		return right ? "#94a3b8" : "#64748b";
	};

	return (
		<div
			style={{
				flex: 1,
				display: "flex",
				flexDirection: "column",
				gap: useGridLayout ? (matchDensity === "compact" ? 4 : 6) : 8,
				minHeight: 0,
				overflow: "hidden",
			}}
		>
			<div
				style={{
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
				}}
			>
				{useGridLayout ? (
					<div
						style={{
							flex: 1,
							minHeight: 0,
							display: "grid",
							gridTemplateColumns:
								!hasImages && itemCount >= 4
									? "repeat(2, minmax(0, 1fr))"
									: "repeat(auto-fit, minmax(min(100%, 120px), 1fr))",
							gridAutoRows: !hasImages && itemCount >= 4 ? "minmax(0, 1fr)" : undefined,
							gap: densitySizes.gridGap,
							alignItems: "stretch",
						}}
					>
						{leftItems.map((left, idx) => {
							const rightId = slotAssignments[left.id];
							const right = rightId ? getRightById(rightId) : null;
							const isCorrect = done && rightId && correctPairs.has(`${left.id}:${rightId}`);
							const isWrong = done && rightId && !correctPairs.has(`${left.id}:${rightId}`);
							return (
								<div
									key={left.id}
									style={{
										display: "flex",
										flexDirection: "column",
										alignItems: "stretch",
										gap: hasImages ? 6 : 4,
										minWidth: 0,
										minHeight: 0,
										boxSizing: "border-box",
									}}
								>
									<div
										onDragOver={handleDragOver}
										onDrop={(e) => handleSlotDrop(e, left.id)}
										onDragStart={right && !done ? (e) => handleSlotDragStart(e, left.id, right.id) : undefined}
										draggable={!!right && !done}
										style={{
											flex: hasImages ? "0 0 auto" : "1 1 0",
											minWidth: 0,
											minHeight: hasImages ? densitySizes.slotMinH : 0,
											padding: densitySizes.rowPad,
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											borderRadius: 10,
											border: `2px dashed ${slotBorder(right, isCorrect, isWrong)}`,
											background: slotBg(right),
											cursor: !done ? "pointer" : "default",
											...optionTextStyle,
											boxSizing: "border-box",
											position: "relative",
											overflow: "hidden",
										}}
									>
										{right ? (
											hasImages ? (
												<div style={{ width: "100%", minWidth: 0 }}>
													{renderMatchFace(right, { imgMaxH: 68, minImgBox: 0 })}
													{(isCorrect || isWrong) && (
														<span
															style={{
																position: "absolute",
																top: 6,
																right: 6,
																color: isCorrect ? "#22c55e" : "#ef4444",
																fontWeight: "bold",
																fontSize: 16,
															}}
														>
															{isCorrect ? "✓" : "✗"}
														</span>
													)}
												</div>
											) : (
												<span style={{ flex: 1, minWidth: 0, textAlign: "center", lineHeight: densitySizes.textLineHeight, wordBreak: "break-word", overflowWrap: "anywhere" }}>
													{matchHasText(right) ? right.text : "—"}
												</span>
											)
										) : (
											<span style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600, textAlign: "center" }}>Suelta aquí</span>
										)}
									</div>
									<div
										style={{
											flex: "0 0 auto",
											minWidth: 0,
											borderRadius: 10,
											border: cardBorder,
											background: hasImages ? "transparent" : matchItemFillBg(left),
											padding: densitySizes.rowPad,
											boxSizing: "border-box",
											overflow: "hidden",
										}}
									>
										{hasImages ? (
											renderMatchFace(left, { bracketLabel: `[${idx + 1}]`, imgMaxH: 72, minImgBox: 0 })
										) : (
											<span style={{ display: "block", textAlign: "center", lineHeight: densitySizes.textLineHeight, wordBreak: "break-word", overflowWrap: "anywhere", fontWeight: optionTextStyle?.fontWeight ?? 600, ...optionTextStyle }}>
												{matchHasText(left) ? left.text : "—"}
											</span>
										)}
									</div>
								</div>
							);
						})}
					</div>
				) : (
					<div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: densitySizes.rowGap, overflow: "hidden" }}>
					{leftItems.map((left) => {
						const rightId = slotAssignments[left.id];
						const right = rightId ? getRightById(rightId) : null;
						const isCorrect = done && rightId && correctPairs.has(`${left.id}:${rightId}`);
						const isWrong = done && rightId && !correctPairs.has(`${left.id}:${rightId}`);
						const imgL = matchResolvedPortalImg(left);
						return (
							<div key={left.id} style={{ display: "flex", alignItems: "stretch", gap: 6, flex: "1 1 0", minHeight: 0 }}>
								<div
									style={{
										flex: 1,
										display: "flex",
										alignItems: "center",
										gap: 8,
										padding: densitySizes.rowPad,
										background: matchItemFillBg(left),
										borderRadius: 8,
										border: "1px solid #e2e8f0",
										minHeight: 0,
										boxSizing: "border-box",
										overflow: "hidden",
										...optionTextStyle,
									}}
								>
									{imgL ? (
										<img
											src={imgL}
											alt=""
											draggable={false}
											onError={imgErr}
											style={{ width: 48, height: 48, flexShrink: 0, objectFit: "contain", borderRadius: 6 }}
										/>
									) : null}
									<span style={{ flex: 1, lineHeight: densitySizes.textLineHeight, wordBreak: "break-word", overflowWrap: "anywhere" }}>{matchHasText(left) ? left.text : imgL ? "" : "—"}</span>
								</div>
								<div
									onDragOver={handleDragOver}
									onDrop={(e) => handleSlotDrop(e, left.id)}
									onDragStart={right && !done ? (e) => handleSlotDragStart(e, left.id, right.id) : undefined}
									draggable={!!right && !done}
									style={{
										flex: 1,
										minHeight: 0,
										display: "flex",
										alignItems: "center",
										gap: 6,
										padding: densitySizes.rowPad,
										background: slotBg(right),
										borderRadius: 8,
										border: `2px dashed ${slotBorder(right, isCorrect, isWrong)}`,
										cursor: !done ? "pointer" : "default",
										overflow: "hidden",
										boxSizing: "border-box",
										...optionTextStyle,
									}}
								>
									{right ? (
										<>
											{matchResolvedPortalImg(right) ? (
												<img
													src={matchResolvedPortalImg(right)}
													alt=""
													draggable={false}
													onError={imgErr}
													style={{ width: 40, height: 40, flexShrink: 0, objectFit: "contain", borderRadius: 6 }}
												/>
											) : null}
											<span style={{ flex: 1, lineHeight: densitySizes.textLineHeight, wordBreak: "break-word", overflowWrap: "anywhere" }}>{matchHasText(right) ? right.text : matchResolvedPortalImg(right) ? "" : "—"}</span>
											{(isCorrect || isWrong) && (
												<span style={{ color: isCorrect ? "#22c55e" : "#ef4444", fontWeight: "bold" }}>{isCorrect ? "✓" : "✗"}</span>
											)}
										</>
									) : (
										<span style={{ color: "#94a3b8" }}>Suelta aquí</span>
									)}
								</div>
							</div>
						);
					})}
					</div>
				)}
			</div>
			{!done && (
				<div
					onDragOver={handleDragOver}
					onDrop={handlePoolDrop}
					style={{
						flexShrink: 0,
						padding: useGridLayout ? "4px 2px 0" : "8px 0",
						display: "flex",
						flexDirection: "column",
						gap: useGridLayout ? 4 : 8,
						minHeight: 0,
						overflow: "visible",
					}}
				>
					<Box fontSize={10} color="#94a3b8" flexShrink={0}>
						Arrastra estas opciones:
					</Box>
					<div
						style={
							textPoolGrid
								? {
									display: "grid",
									gridTemplateColumns: availablePoolCount >= 4 ? "repeat(2, minmax(0, 1fr))" : "repeat(auto-fit, minmax(0, 1fr))",
									gap: densitySizes.gridGap,
									width: "100%",
									flexShrink: 0,
									overflow: "visible",
								}
								: {
									display: "flex",
									flexDirection: "row",
									flexWrap: useGridLayout ? "nowrap" : "wrap",
									gap: densitySizes.gridGap,
									alignItems: "flex-start",
									overflowX: useGridLayout ? "auto" : "visible",
									overflowY: "visible",
									WebkitOverflowScrolling: "touch",
									minHeight: 0,
								}
						}
					>
						{availablePool.map((rightId) => {
							const r = getRightById(rightId);
							if (!r) return null;
							return (
								<div
									key={rightId}
									draggable
									onDragStart={(ev) => { ev.currentTarget.style.cursor = "grabbing"; handleDragStart(ev, rightId); }}
									onDragEnd={(e) => { e.currentTarget.style.cursor = "grab"; }}
									style={
										useGridLayout
											? hasImages
												? {
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												gap: 4,
												padding: "8px 8px 6px",
												borderRadius: 10,
												border: "1px solid rgba(148, 163, 184, 0.55)",
												background: matchHasText(r) ? "rgba(248, 250, 252, 0.95)" : "transparent",
												cursor: "grab",
												...optionTextStyle,
												transition: "all 0.15s ease",
												flex: "0 0 auto",
												width: densitySizes.poolW || 128,
												minWidth: densitySizes.poolMinW,
												maxWidth: densitySizes.poolMaxW,
												boxSizing: "border-box",
											}
												: {
												display: "flex",
												flexDirection: "row",
												alignItems: "flex-start",
												gap: 4,
												padding: "6px 8px",
												borderRadius: 8,
												border: "1px solid rgba(148, 163, 184, 0.55)",
												background: "rgba(248, 250, 252, 0.95)",
												cursor: "grab",
												...optionTextStyle,
												transition: "all 0.15s ease",
												width: "100%",
												minWidth: 0,
												height: "auto",
												overflow: "visible",
												boxSizing: "border-box",
											}
											: {
												display: "flex",
												alignItems: "center",
												gap: 6,
												padding: "8px 14px",
												background: "#fff",
												borderRadius: 8,
												border: "1px solid #e2e8f0",
												cursor: "grab",
												...optionTextStyle,
												transition: "all 0.15s ease",
											}
									}
									onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; }}
									onMouseLeave={(e) => { e.currentTarget.style.borderColor = useGridLayout ? "rgba(148, 163, 184, 0.55)" : "#e2e8f0"; e.currentTarget.style.boxShadow = "none"; }}
									title="Arrastra a la zona de emparejamiento"
								>
									{useGridLayout ? (
										hasImages ? (
										<>
											<div style={{ width: "100%", minWidth: 0, flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
												{renderMatchFace(r, { imgMaxH: 72, minImgBox: 0 })}
											</div>
											<DragIndicatorIcon style={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />
										</>
										) : (
										<>
											<span style={{ flex: 1, minWidth: 0, lineHeight: densitySizes.textLineHeight, wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal" }}>{r.text || "—"}</span>
											<DragIndicatorIcon style={{ fontSize: 14, color: "#94a3b8", flexShrink: 0, marginTop: 1 }} />
										</>
										)
									) : (
										<>
											{r.imageUrl && r.imageUrl.trim() ? (
												<img
													src={resolveQuizImageUrl(r.imageUrl)}
													alt=""
													style={{ width: 28, height: 28, objectFit: "contain", borderRadius: 4 }}
													onError={imgErr}
													draggable={false}
												/>
											) : null}
											<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.text || "—"}</span>
											<DragIndicatorIcon style={{ fontSize: 14, color: "#cbd5e1", flexShrink: 0 }} />
										</>
									)}
								</div>
							);
						})}
					</div>
				</div>
			)}
			{!done && canCheck && (
				<Button
					variant="contained"
					color="primary"
					onClick={handleCheck}
					startIcon={<CheckCircleIcon style={{ fontSize: 18 }} />}
					style={{
						flexShrink: 0,
						alignSelf: "flex-start",
						padding: "10px 24px",
						fontSize: 14,
						fontWeight: 600,
						borderRadius: 10,
						textTransform: "none",
						boxShadow: "0 2px 8px rgba(124, 58, 237, 0.3)",
					}}
				>
					Comprobar
				</Button>
			)}
		</div>
	);
}

function FillBlanksSegmentEdit({ seg, segIndex, isBlank, canEdit, onBlur, optionTextStyle }) {
	const value = seg.type === "text" ? (seg.value || "") : (seg.correctAnswer || "");
	const [local, setLocal] = useState(value);
	const isFocusedRef = useRef(false);
	useEffect(() => {
		if (!isFocusedRef.current) setLocal(value);
	}, [value]);
	if (!canEdit) {
		return seg.type === "text" ? <span>{seg.value || ""}</span> : <span style={{ display: "inline-block", minWidth: 60, padding: "2px 8px", background: "#e2e8f0", borderRadius: 4, border: "1px dashed #94a3b8", color: "#64748b" }}>____</span>;
	}
	const baseStyle = seg.type === "text"
		? { outline: "none", border: "none", background: "transparent", minWidth: 24, width: "auto", maxWidth: 200, ...optionTextStyle, font: "inherit" }
		: { display: "inline-block", minWidth: 60, padding: "4px 10px", borderRadius: 6, border: "2px dashed #94a3b8", outline: "none", font: "inherit", fontFamily: optionTextStyle?.fontFamily, fontSize: optionTextStyle?.fontSize, fontWeight: optionTextStyle?.fontWeight, color: "#1e293b", backgroundColor: "#f8fafc", boxSizing: "border-box" };
	return (
		<input
			type="text"
			value={local}
			onChange={(e) => setLocal(e.target.value)}
			onFocus={() => { isFocusedRef.current = true; }}
			onBlur={(e) => {
				isFocusedRef.current = false;
				const v = e.target.value.trim();
				setLocal(seg.type === "text" ? v : (v || ""));
				if (seg.type === "text" && v !== (seg.value || "")) onBlur(segIndex, false)({ target: { textContent: v } });
				else if (seg.type === "blank" && v !== (seg.correctAnswer || "")) onBlur(segIndex, true)({ target: { textContent: v } });
			}}
			onClick={(e) => e.stopPropagation()}
			onMouseDown={(e) => e.stopPropagation()}
			placeholder={seg.type === "blank" && !value ? "____" : ""}
			style={baseStyle}
		/>
	);
}

function FillBlanksQuizPlay({ firstQ, onConfirm, done, answer, optionTextStyle }) {
	const segments = firstQ?.segments || [];
	const blanks = segments.filter((s) => s.type === "blank");
	const [shuffledOptions] = useState(() => shuffleArray([...(firstQ?.fillOptions || [])]));
	const options = shuffledOptions;
	const [blankAnswers, setBlankAnswers] = useState({});
	useEffect(() => {
		if (done && answer?.blanks?.length) {
			const obj = {};
			answer.blanks.forEach((b) => { obj[b.blankId] = b.answer; });
			setBlankAnswers(obj);
			return;
		}
		if (!done) setBlankAnswers({});
	}, [firstQ?.segments, firstQ?.fillOptions, done, answer?.blanks]);
	const handleDragStart = (e, opt) => {
		e.dataTransfer.setData("application/json", JSON.stringify({ type: "option", value: opt }));
		e.dataTransfer.effectAllowed = "copy";
	};
	const handleBlankDragStart = (e, blankId) => {
		const val = blankAnswers[blankId];
		if (!val) return;
		e.dataTransfer.setData("application/json", JSON.stringify({ type: "fromBlank", blankId, value: val }));
		e.dataTransfer.effectAllowed = "move";
	};
	const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; };
	const handleBlankDrop = (e, blankId) => {
		e.preventDefault();
		try {
			const d = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
			if (d.type === "option") {
				setBlankAnswers((prev) => ({ ...prev, [blankId]: d.value }));
			} else if (d.type === "fromBlank") {
				setBlankAnswers((prev) => {
					const next = { ...prev };
					delete next[d.blankId];
					next[blankId] = d.value;
					return next;
				});
			}
		} catch (_) {}
	};
	const handleOptionDrop = (e) => {
		e.preventDefault();
		try {
			const d = JSON.parse(e.dataTransfer.getData("application/json") || "{}");
			if (d.type === "fromBlank") {
				setBlankAnswers((prev) => {
					const next = { ...prev };
					delete next[d.blankId];
					return next;
				});
			}
		} catch (_) {}
	};
	const filledCount = blanks.filter((b) => blankAnswers[b.blankId]).length;
	const canConfirm = filledCount === blanks.length && blanks.length > 0;
	const handleConfirm = () => {
		if (!canConfirm || !onConfirm) return;
		onConfirm({ blanks: blanks.map((b) => ({ blankId: b.blankId, answer: blankAnswers[b.blankId] || "" })), confirmed: true });
	};
	const correctBlanks = new Set();
	if (done && answer?.blanks) {
		(answer.blanks || []).forEach((b) => {
			const seg = blanks.find((s) => s.blankId === b.blankId);
			if (seg && String((seg.correctAnswer || "").trim()).toLowerCase() === String((b.answer || "").trim()).toLowerCase()) {
				correctBlanks.add(b.blankId);
			}
		});
	}
	return (
		<div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12, minHeight: 0, overflow: "auto" }}>
			<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, ...optionTextStyle, lineHeight: 1.6 }}>
				{segments.map((seg, i) =>
					seg.type === "text" ? (
						<span key={i}>{seg.value || ""}</span>
					) : (
						<span
							key={i}
							onDragOver={handleDragOver}
							onDrop={(e) => handleBlankDrop(e, seg.blankId)}
							onDragStart={blankAnswers[seg.blankId] ? (e) => handleBlankDragStart(e, seg.blankId) : undefined}
							draggable={!!blankAnswers[seg.blankId] && !done}
							style={{
								display: "inline-block", minWidth: 70, padding: "4px 10px", margin: "0 2px",
								background: blankAnswers[seg.blankId] ? "#fff" : "#f1f5f9", borderRadius: 6,
								border: `2px dashed ${blankAnswers[seg.blankId] ? "#94a3b8" : "#cbd5e1"}`,
								color: blankAnswers[seg.blankId] ? "#334155" : "#94a3b8", cursor: !done ? "pointer" : "default",
								borderColor: done && correctBlanks.has(seg.blankId) ? "#22c55e" : done && blankAnswers[seg.blankId] && !correctBlanks.has(seg.blankId) ? "#ef4444" : undefined,
							}}
						>
							{blankAnswers[seg.blankId] || "____"}
						</span>
					)
				)}
			</div>
			{!done && (
				<div
					onDragOver={handleDragOver}
					onDrop={handleOptionDrop}
					style={{ padding: "12px 0", display: "flex", flexDirection: "column", gap: 10 }}
				>
					<Box fontSize={11} color="#94a3b8">Arrastra cada opción a un hueco:</Box>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
						{options.map((opt, i) => (
							<div
								key={i}
								draggable
								onDragStart={(e) => handleDragStart(e, opt)}
								style={{
									display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
									background: "#fff", borderRadius: 8, border: "1px solid #e2e8f0",
									cursor: "grab", fontSize: (optionTextStyle.fontSize || 12) * 0.95, color: "#1e293b", fontWeight: 500, transition: "all 0.15s ease",
								}}
								onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; }}
								onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.boxShadow = "none"; }}
								title="Arrastra a un hueco"
							>
								{opt}
								<DragIndicatorIcon style={{ fontSize: 14, color: "#cbd5e1", flexShrink: 0 }} />
							</div>
						))}
					</div>
				</div>
			)}
			{!done && canConfirm && (
				<Button
					variant="contained"
					color="primary"
					onClick={handleConfirm}
					startIcon={<CheckCircleIcon style={{ fontSize: 18 }} />}
					style={{
						alignSelf: "flex-start",
						padding: "10px 24px",
						fontSize: 14,
						fontWeight: 600,
						borderRadius: 10,
						textTransform: "none",
						boxShadow: "0 2px 8px rgba(124, 58, 237, 0.3)",
					}}
				>
					Confirmar
				</Button>
			)}
		</div>
	);
}

export function normalizeQuizTextAnswer(value) {
	return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

export function quizTextAnswersMatch(submitted, expected) {
	return normalizeQuizTextAnswer(submitted) === normalizeQuizTextAnswer(expected);
}

export function resolveQuizAttemptMode(quiz) {
	return quiz?.attemptMode || (quiz?.allowMultipleAttempts ? "unlimited" : "single");
}

/** ¿Quiz custom (textAnswer / fillBlanks / match) completado según modo de intentos? */
export function resolveCustomQuizDone({ quiz, isSurvey, allCorrect, attemptCount }) {
	const attemptMode = resolveQuizAttemptMode(quiz);
	const maxAttempts = Math.max(1, Number(quiz?.maxAttempts) || 3);
	if (isSurvey) {
		if (attemptMode === "single") return true;
		if (attemptMode === "limited") return attemptCount >= maxAttempts;
		return attemptCount >= 1;
	}
	if (attemptMode === "single") return true;
	if (attemptMode === "limited") return allCorrect || attemptCount >= maxAttempts;
	return allCorrect;
}

export function accumulateCustomQuizPoints({ quiz, isSurvey, isCorrect, pointsCorrect, pointsIncorrect, previousTotal = 0 }) {
	if (isSurvey) return 0;
	const delta = isCorrect ? pointsCorrect : pointsIncorrect;
	if (resolveQuizAttemptMode(quiz) === "single") return delta;
	return previousTotal + delta;
}

export function TextAnswerQuizPlay({
	firstQ,
	onConfirm,
	done,
	answer,
	optionTextStyle,
	placeholder,
	fieldContainerStyle,
	fieldInputStyle,
	fieldClassName,
	fieldSubmitStyle,
	fieldSubmitClassName,
}) {
	const [text, setText] = useState("");

	useEffect(() => {
		if (done && answer?.text != null) {
			setText(String(answer.text));
			return;
		}
		if (!done) setText("");
	}, [done, answer?.text]);

	const handleSubmit = () => {
		if (done || !onConfirm) return;
		const trimmed = String(text || "").trim();
		if (!trimmed) return;
		onConfirm({ text: trimmed });
	};

	const inputStyle = {
		...optionTextStyle,
		...fieldInputStyle,
		flex: 1,
		minWidth: 0,
		minHeight: 0,
		width: "100%",
		height: "100%",
		alignSelf: "stretch",
		border: fieldInputStyle?.border ?? "none",
		background: fieldInputStyle?.background ?? "transparent",
		outline: "none",
		boxShadow: "none",
		borderRadius: fieldInputStyle?.borderRadius ?? "inherit",
		WebkitAppearance: "none",
		appearance: "none",
		boxSizing: "border-box",
	};

	const containerStyle = {
		flex: 1,
		minHeight: 0,
		minWidth: 0,
		alignSelf: "stretch",
		display: "flex",
		alignItems: "stretch",
		boxSizing: "border-box",
		overflow: "hidden",
		...fieldContainerStyle,
	};

	const submitFontSize = optionTextStyle?.fontSize || 13;
	const canSubmit = !!String(text || "").trim();

	return (
		<div
			style={{
				flex: 1,
				minHeight: 0,
				width: "100%",
				display: "flex",
				flexDirection: "row",
				alignItems: "stretch",
				gap: 8,
				overflow: "hidden",
				boxSizing: "border-box",
			}}
			onMouseDown={(e) => e.stopPropagation()}
			onClick={(e) => e.stopPropagation()}
		>
			<div className={fieldClassName || undefined} style={containerStyle}>
				<input
					type="text"
					value={text}
					onChange={(e) => { if (!done) setText(e.target.value); }}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							handleSubmit();
						}
					}}
					disabled={done}
					placeholder={placeholder || "Escribe tu respuesta…"}
					style={inputStyle}
				/>
			</div>
			{!done && onConfirm ? (
				<button
					type="button"
					className={fieldSubmitClassName || undefined}
					onClick={handleSubmit}
					disabled={!canSubmit}
					style={{
						flexShrink: 0,
						alignSelf: "stretch",
						minWidth: 72,
						fontWeight: 600,
						fontSize: submitFontSize,
						cursor: canSubmit ? "pointer" : "not-allowed",
						fontFamily: optionTextStyle?.fontFamily || "inherit",
						opacity: canSubmit ? 1 : 0.55,
						WebkitAppearance: "none",
						appearance: "none",
						margin: 0,
						...fieldSubmitStyle,
					}}
				>
					Enviar
				</button>
			) : null}
		</div>
	);
}

export { MatchQuizPlay, FillBlanksSegmentEdit, FillBlanksQuizPlay };
